import clsx from 'clsx';
import React, { useState } from 'react';
import { FieldInputProps, FieldMetaProps } from 'formik';

interface InputFieldPasswordProps {
    field?: FieldInputProps<string>;
    meta?: FieldMetaProps<string>;
    value?: string;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    className?: string;
    placeholder?: string;
    autoComplete?: string;
    id?: string;
    name?: string;
    disabled?: boolean;
    readOnly?: boolean;
    error?: string;
}

export function InputFieldPassword({
    field,
    meta,
    value,
    onChange,
    onBlur,
    className,
    placeholder,
    autoComplete = 'off',
    id,
    name,
    disabled = false,
    readOnly = false,
    error,
}: InputFieldPasswordProps) {
    const [showPassword, setShowPassword] = useState(false);

    const togglePasswordVisibility = () => {
        setShowPassword((prev) => !prev);
    };

    const isFormikMode = !!field;

    const hasError = isFormikMode ? meta?.touched && meta?.error : !!error;
    const isValid = isFormikMode ? meta?.touched && !meta?.error : false;

    const inputProps = isFormikMode
        ? {
              ...field,
              id: id || field.name,
              name: name || field.name,
          }
        : {
              value: value || '',
              onChange: onChange,
              onBlur: onBlur,
              id: id,
              name: name,
              disabled: disabled,
              readOnly: readOnly,
          };

    return (
        <div className='position-relative w-100'>
            <input
                {...inputProps}
                type={showPassword ? 'text' : 'password'}
                placeholder={placeholder}
                autoComplete={autoComplete}
                className={clsx(
                    isFormikMode
                        ? 'form-control form-control-lg form-control-solid'
                        : 'form-control',
                    {
                        'is-invalid': hasError,
                    },
                    {
                        'is-valid': isValid,
                    },
                    className
                )}
            />
            <button
                type='button'
                className={clsx(
                    'btn btn-icon btn-sm position-absolute top-50 translate-middle-y end-0 border-0 bg-transparent',
                    {
                        'me-8': isValid,
                        'me-4': !isValid,
                    }
                )}
                onClick={togglePasswordVisibility}
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                style={{ cursor: 'pointer', zIndex: 10 }}
                disabled={disabled || readOnly}>
                <i
                    className={clsx('bi text-gray-500', {
                        'bi-eye-slash': showPassword,
                        'bi-eye': !showPassword,
                    })}></i>
            </button>
        </div>
    );
}
